import { serverTimestamp, Timestamp } from "firebase/firestore";
import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import type { UserReaction } from "../../activities/ActivityModel";
import { REACTIONS, type Reaction } from "../../activities/types";
import type { Api } from "../../core/apis";
import { BaseModel } from "../../core/BaseModel";
import type { Stores } from "../../core/stores";
import { Permission } from "../../permissions/types";
import type { IconProp } from "../../ui/icons";
import type {
  BaseFeedModelData,
  BusinessFeedMention,
} from "../feed/BusinessFeedItem";
import { buildFeedMentionsFromText } from "../feed/utils";
import {
  type BusinessFeedCommentReplyData,
  BusinessFeedCommentReplyModel,
} from "../feedcommentreply/BusinessFeedCommentReplyModel";
import type { BusinessStaticUserInfoWithUserUid } from "../plan/BusinessPlanModel";

const MAX_REPLIES_IN_CACHE = 2;

export interface BusinessFeedCommentData extends BaseFeedModelData {
  businessPlanUid: string;
  feedItemUid: string;
  authorUserUid: string;

  reactions: UserReaction[];
  mentions: BusinessFeedMention[];
  mentionUids: string[]; // for efficient query

  text: string;

  repliesCount: number;
  latestReplies: BusinessFeedCommentReplyData[]; // Cache of latest replies
}

export class BusinessFeedCommentModel extends BaseModel<BusinessFeedCommentData> {
  constructor(stores: Stores, api: Api, data: BusinessFeedCommentData) {
    super(stores, api, data);

    Object.assign(this.data, {
      mentions: data.mentions ?? [],
      mentionUids: data.mentionUids ?? [],
      latestReplies: data.latestReplies ?? [],
      repliesCount: data.repliesCount ?? 0,

      hidden: this.data.hidden ?? false,
      deleted: this.data.deleted ?? false,
      edited: this.data.edited ?? false,
      reported: this.data.reported ?? 0,
    });

    this.loadCache();

    makeObservable(this);
  }

  loadCache() {
    // Add cached items as models
    const commentReplies = this.data.latestReplies.map(
      (d) => new BusinessFeedCommentReplyModel(this.stores, this.api, d),
    );
    this.stores.businessFeedCommentsReplyStore.load(commentReplies);
  }

  @computed get replies(): BusinessFeedCommentReplyModel[] {
    const items: BusinessFeedCommentReplyModel[] = [];

    const data = this.stores.businessFeedCommentsReplyStore.all;

    for (const d of data) {
      if (d.data.feedCommentUid === this.uid) {
        items.push(d);
      }
    }

    return items.sort((a, b) => ((a.data.ts ?? 0) > (b.data.ts ?? 0) ? 1 : -1));
  }

  @computed get maybeAuthor(): BusinessStaticUserInfoWithUserUid | undefined {
    const authorUid = this.data.authorUserUid;
    if (authorUid) {
      const u = this.feedItem.businessPlan?.data.userInfo[authorUid];

      if (u) {
        return { ...u, userUid: authorUid };
      }
    }
    return undefined;
  }

  @computed get isAuthor(): boolean {
    const author = this.maybeAuthor;
    const user = this.feedItem.businessPlan?.userInPlan;

    return !!user && !!author && author.userUid === user.uid;
  }

  @computed get isAdmin(): boolean {
    return this.feedItem.businessPlan.checkPermission(
      Permission.CanManageFeedItems,
    );
  }

  @computed get deletedCount(): number {
    return this.replies.filter((r) => r.data.deleted).length;
  }

  @computed get hasMoreReplies(): boolean {
    return this.replies.length < this.data.repliesCount;
  }

  @action async edit(text: string) {
    this.data.text = text;

    const { mentions, mentionUids } = buildFeedMentionsFromText(
      text,
      this.feedItem.businessPlan,
    );
    this.data.mentions = mentions;
    this.data.mentionUids = mentionUids;

    await this.api.businessFeedCommentsApi.set(this);

    return this.saveEditAction("edited");
  }

  @action async saveEditAction(
    option: "edited" | "deleted" | "hidden" | "reported",
  ) {
    const businessPlan = this.feedItem.businessPlan;

    if (!businessPlan) {
      return undefined;
    }

    if (option === "edited") this.data.edited = true;
    if (option === "deleted") this.data.deleted = true;
    if (option === "hidden") this.data.hidden = !this.data.hidden;
    if (option === "reported") this.data.reported++;

    await this.api.businessFeedCommentsApi.update(this.data.uid, {
      edited: this.data.edited,
      deleted: this.data.deleted,
      hidden: this.data.hidden,
      reported: this.data.reported,
    });

    void this.feedItem.updateFeedCommentInCache(this);
  }

  @action async toggleReaction(reaction: Reaction) {
    const feedItem = this.feedItem;
    const currentUserUid = feedItem?.businessPlan?.userInPlan?.data.uid;

    if (!currentUserUid || !feedItem) {
      return undefined;
    }

    const hasReacted = this.data.reactions.findIndex(
      (r) => r.reaction === reaction && r.userUid === currentUserUid,
    );

    if (hasReacted !== -1) {
      this.data.reactions.splice(hasReacted, 1);
    } else {
      this.data.reactions.push({
        userUid: currentUserUid,
        reaction,
      });
    }

    await this.api.businessFeedCommentsApi.update(this.data.uid, {
      reactions: this.data.reactions,
    });

    void feedItem.updateFeedCommentInCache(this);
  }

  @computed get reactionRowItemCounts(): {
    icon: IconProp | string;
    count: number;
  }[] {
    const anyReaction: { icon: IconProp | string; count: number }[] = [];
    REACTIONS.forEach((r) => {
      const c = this.data.reactions.filter(
        (rr) => rr.reaction === r.type,
      ).length;
      if (c) {
        anyReaction.push({
          icon: r.icon,
          count: c,
        });
      }
    });
    return anyReaction;
  }

  @computed get feedItem() {
    const f = this.stores.businessFeedItemsStore.byId.get(
      this.data.feedItemUid,
    );

    if (!f) {
      throw Error("FeedItem not found");
    }

    return f;
  }

  @computed get author(): BusinessStaticUserInfoWithUserUid | undefined {
    const i =
      this.feedItem?.businessPlan?.data.userInfo[this.data.authorUserUid];

    if (i) {
      return { ...i, userUid: this.data.authorUserUid };
    }

    return undefined;
  }

  @action async addReplyComment(
    item: Omit<
      BusinessFeedCommentReplyData,
      | "ts"
      | "reactions"
      | "mentions"
      | "mentionUids"
      | "authorUserUid"
      | "created"
      | "uid"
      | "feedCommentUid"
      | "businessPlanUid"
      | "feedItemUid"
      | "edited"
      | "deleted"
      | "hidden"
      | "pinned"
      | "reported"
    >,
  ) {
    const feedItem = this.feedItem;
    const businessPlan = feedItem?.businessPlan;
    const currentUserUid = businessPlan?.userInPlan?.data.uid;

    if (!currentUserUid || !feedItem || !businessPlan) {
      return undefined;
    }

    const feedCommentReply: Omit<BusinessFeedCommentReplyData, "uid"> = {
      ...item,
      ts: serverTimestamp() as Timestamp,
      businessPlanUid: businessPlan.uid,
      feedItemUid: this.feedItem.uid,
      feedCommentUid: this.data.uid,
      authorUserUid: currentUserUid,
      reactions: [],
      hidden: false,
      deleted: false,
      pinned: false,
      edited: false,
      reported: 0,
      ...buildFeedMentionsFromText(item.text, businessPlan),
    };

    const feedCommentReplyModel =
      await this.api.businessFeedCommentsReplyApi.add(feedCommentReply);

    // Save a cache in item
    this.data.latestReplies.push({
      ...feedCommentReplyModel.toFireStore(),
      uid: feedCommentReplyModel.uid,
    });

    this.data.repliesCount++;
    if (this.data.latestReplies.length > MAX_REPLIES_IN_CACHE) {
      this.data.latestReplies.shift();
    }

    await this.api.businessFeedCommentsApi.update(this.data.uid, {
      repliesCount: this.data.repliesCount,
      latestReplies: this.data.latestReplies,
    });

    void feedItem.updateFeedCommentInCache(this);

    const user = feedItem.businessPlan.userInPlan;
    const parentAuthor = this.author;
    const feedItemAuthor = feedItem.maybeAuthor;

    if (user && parentAuthor && feedItemAuthor) {
      void feedItem.businessPlan.addEmailData({
        item: {
          uid: feedCommentReplyModel.uid,
          ts: feedCommentReplyModel.data.ts ?? Timestamp.now(),

          author: {
            uid: user.uid,
            name: user.data.displayName,
          },
          type: "feed_comment_reply",
          textSID: TKey.BusinessFeedCommentReplyPosted,
          textData: { comment: feedCommentReplyModel.data.text },

          parent: {
            uid: this.uid,
            ts: this.data.ts ?? Timestamp.now(),
            author: {
              uid: parentAuthor.userUid,
              name: parentAuthor.displayName,
            },
            type: "feed_comment",
            textSID: TKey.BusinessFeedCommentPosted,
            textData: { comment: this.data.text },

            parent: {
              uid: feedItem.uid,
              ts: feedItem.data.ts ?? Timestamp.now(),
              author: {
                uid: feedItemAuthor.userUid,
                name: feedItemAuthor.displayName,
              },
              type: "feed",
              textSID: TKey.BusinessFeedItemPosted,
              textData: { comment: feedItem.textRepresentationInEmail },
            },
          },
        },
      });
    }
  }

  @action updateFeedReplyCommentInCache(item: BusinessFeedCommentReplyModel) {
    const inCacheIndex = this.data.latestReplies.findIndex(
      (f) => f.uid === item.uid,
    );

    if (inCacheIndex !== -1) {
      // Update in place
      this.data.latestReplies[inCacheIndex] = {
        ...item.toFireStore(),
        uid: item.uid,
      };
      void this.api.businessFeedCommentsApi.update(this.uid, {
        latestReplies: this.data.latestReplies,
      });
    }

    this.feedItem.updateFeedCommentInCache(this);
  }

  toFireStore(): Omit<BusinessFeedCommentData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
